import { useMemo, useEffect, useRef, useLayoutEffect, useState } from 'react';
import { useNodePositions } from './useNodePositions';
import { useEffectiveChainInfo } from './useEffectiveChainInfo';
import { useCompactAfterDelete } from './useCompactAfterDelete';
import { useVerticalSpacingOnResize } from './useVerticalSpacingOnResize';
import { useEdgePathCache } from './useEdgePathCache';
import { generateEdges } from '../utils/edgePaths';
import { digestNodePositionsRecord } from '../utils/positionDigest';
import { calculateLockStatesWithParentContext } from '../utils/dependencyUtils';
import { useWaitGateNow } from '../../shared/ConditionsDropdown/utils/useWaitGateNow';
import { useElkEdgePathsForCache } from './flowchartCore/elkCustomPositionHeuristics';

/**
 * Merged node positions, compaction, edges, lock states, chain info for rendering.
 */
export function useFlowchartCoreGraphCompaction({
  tasks,
  nestedSprints,
  statuses,
  localSprint,
  allSprints,
  interaction,
  autoPositions,
  useAutoPositions,
  elkPositionsOk,
  elkEdgePaths,
  chainInfo,
  nodeDimensions,
  isMultiCampaignMode,
  onPositionChangeBatch,
  wrapLastUndoAction,
  onApplyTaskPositions,
  hasActiveFilters,
  pan,
  zoom,
  containerRef,
  setPanAnimated,
  autoPan,
  afterDeleteHandlerRef,
  suppressEdgesUntilElkReady = false,
}) {
  const compactAnimationStartRef = useRef(null);
  const compactAnimationEndRef = useRef(null);

  const dragLayoutTick = interaction.dragLayoutTick ?? 0;
  const currentDragPositionsRef = interaction.currentDragPositionsRef ?? null;

  const baseNodePositions = useNodePositions({
    tasks,
    autoPositions,
    customPositions: interaction.customPositions,
    isMultiCampaignMode,
    useAutoPositions,
    hasActiveFilters,
    elkPositionsOk,
  });

  const nodePositions = useMemo(() => {
    const drag = currentDragPositionsRef?.current;
    if (!drag || Object.keys(drag).length === 0) return baseNodePositions;
    return { ...baseNodePositions, ...drag };
  }, [baseNodePositions, dragLayoutTick, currentDragPositionsRef]);

  useEffect(() => {
    if (autoPan.panRef) autoPan.panRef.current = pan;
    if (autoPan.zoomRef) autoPan.zoomRef.current = zoom;
    if (autoPan.containerRefRef) autoPan.containerRefRef.current = containerRef;
    if (autoPan.setPanAnimatedRef) autoPan.setPanAnimatedRef.current = setPanAnimated;
    if (autoPan.nodePositionsRef) autoPan.nodePositionsRef.current = nodePositions;
    if (autoPan.nodeDimensionsRef) autoPan.nodeDimensionsRef.current = nodeDimensions;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pan, zoom, containerRef, setPanAnimated, nodePositions, nodeDimensions]);

  const compact = useCompactAfterDelete({
    tasks,
    nodePositions: baseNodePositions,
    nodeDimensions,
    setCustomPositions: interaction.setCustomPositions,
    onPositionChangeBatch,
    wrapLastUndoAction,
    onApplyTaskPositions,
    onAnimationStart: () => compactAnimationStartRef.current?.(),
    onAnimationEnd: () => compactAnimationEndRef.current?.(),
    isMultiCampaignMode,
  });

  useVerticalSpacingOnResize({
    tasks,
    nodePositions: baseNodePositions,
    nodeDimensions,
    setCustomPositions: interaction.setCustomPositions,
    onPositionChangeBatch,
    onAnimationStart: () => compactAnimationStartRef.current?.(),
    onAnimationEnd: () => compactAnimationEndRef.current?.(),
    isMultiCampaignMode,
    // Multi-campaign only. Single campaign handles size changes via the debounced ELK
    // relayout; running this during the pre-settle load window silently persisted
    // push-down nudges to the DB (position drift on every open).
    enabled: isMultiCampaignMode,
  });

  useEffect(() => {
    afterDeleteHandlerRef.current = (deletedIds) => {
      compact.handleCompactAfterDelete(deletedIds);
      autoPan.handleAfterDelete(deletedIds);
    };
  }, [compact.handleCompactAfterDelete, autoPan.handleAfterDelete, afterDeleteHandlerRef]);

  // Bump only when merged node coordinates actually change (avoids full edge regen on object identity churn).
  const [edgeLayoutTick, setEdgeLayoutTick] = useState(0);
  const lastNodePosDigestRef = useRef(null);
  useLayoutEffect(() => {
    const d = digestNodePositionsRecord(nodePositions);
    if (lastNodePosDigestRef.current === null) {
      lastNodePosDigestRef.current = d;
      return;
    }
    if (d !== lastNodePosDigestRef.current) {
      lastNodePosDigestRef.current = d;
      setEdgeLayoutTick((n) => n + 1);
    }
  }, [nodePositions]);

  const nodePositionsForEdgesRef = useRef(nodePositions);
  nodePositionsForEdgesRef.current = nodePositions;

  const edgesRaw = useMemo(
    () => generateEdges(tasks, nodePositionsForEdgesRef.current, nodeDimensions),
    [tasks, nodeDimensions, edgeLayoutTick],
  );
  const elkEdgePathsForCache = useElkEdgePathsForCache({
    elkEdgePaths,
    isMultiCampaignMode,
  });

  const edges = useEdgePathCache(edgesRaw, elkEdgePathsForCache, {
    nodePositions,
    autoPositions,
    // Never bypass endpoint checks for main ELK paths: `isAnimatingLayout` can be true
    // while `elkLayoutResult` is still the *previous* graph, which forces stale orthogonal
    // routes (lines to old anchors) while nodes already follow custom / drag positions.
    useElkPathsDespiteEndpointMismatch: false,
    suppressEdgesUntilElkReady,
  });

  const waitNow = useWaitGateNow(tasks);
  const lockStates = useMemo(
    () =>
      calculateLockStatesWithParentContext(tasks, nestedSprints, statuses, {
        currentSprint: localSprint,
        allSprints,
        now: waitNow,
      }),
    [tasks, nestedSprints, statuses, localSprint, allSprints, waitNow],
  );

  const effectiveChainInfo = useEffectiveChainInfo({
    tasks,
    chainInfo,
    nodePositions,
    nodeDimensions,
  });

  return {
    compactAnimationStartRef,
    compactAnimationEndRef,
    nodePositions,
    edges,
    lockStates,
    effectiveChainInfo,
  };
}
